import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Aperture, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { DECK_VELOCITY_THRESHOLD, resolveDeckSwipeDirection } from "../domain/deck-gesture";
import type { AlbumDocument } from "../domain/project";
import { PhotoFrame } from "./PhotoFrame";

interface CircularGalleryPrototypeProps {
  document: AlbumDocument;
  urls: Record<string, string>;
  embedded?: boolean;
  headingId?: string;
}

const SWIPE_DISTANCE = 44;
const THROW_DURATION = 360;

type CircularMotionMode = "idle" | "dragging" | "throwing";

export function CircularGalleryPrototype({ document, urls, embedded = false, headingId = "circular-prototype-title" }: CircularGalleryPrototypeProps) {
  const pages = document.pages;
  const [activeIndex, setActiveIndex] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [motionMode, setMotionMode] = useState<CircularMotionMode>("idle");
  const dragState = useRef<{ pointerId: number; startX: number; startY: number; lastX: number; lastTime: number; velocityX: number } | null>(null);
  const throwTimer = useRef<number | null>(null);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => {
      setReducedMotion(media.matches);
      if (media.matches && throwTimer.current !== null) {
        window.clearTimeout(throwTimer.current);
        throwTimer.current = null;
        dragState.current = null;
        setDragOffset(0);
        setMotionMode("idle");
      }
    };
    update();
    media.addEventListener?.("change", update);
    return () => {
      media.removeEventListener?.("change", update);
      if (throwTimer.current !== null) window.clearTimeout(throwTimer.current);
    };
  }, []);

  useEffect(() => {
    setActiveIndex((current) => pages.length ? Math.min(current, pages.length - 1) : 0);
  }, [pages.length]);

  const move = (direction: "next" | "prev") => {
    if (motionMode === "throwing" || !pages.length) return;
    setActiveIndex((current) => direction === "next" ? Math.min(current + 1, pages.length - 1) : Math.max(current - 1, 0));
  };

  const resetDrag = () => {
    dragState.current = null;
    setDragOffset(0);
    setMotionMode("idle");
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.currentTarget.dataset.index !== String(activeIndex) || motionMode === "throwing" || (event.pointerType === "mouse" && event.button !== 0)) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragState.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, lastX: event.clientX, lastTime: event.timeStamp, velocityX: 0 };
    setDragOffset(0);
    setMotionMode("dragging");
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const state = dragState.current;
    if (!state || state.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - state.startX;
    const deltaY = event.clientY - state.startY;
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 10) return;
    const elapsed = Math.max(1, event.timeStamp - state.lastTime);
    state.velocityX = (event.clientX - state.lastX) / elapsed;
    state.lastX = event.clientX;
    state.lastTime = event.timeStamp;
    event.preventDefault();
    setDragOffset(deltaX);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    const state = dragState.current;
    if (!state || state.pointerId !== event.pointerId) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const deltaX = event.clientX - state.startX;
    const shouldMove = Math.abs(deltaX) >= SWIPE_DISTANCE || Math.abs(state.velocityX) >= DECK_VELOCITY_THRESHOLD;
    const direction = resolveDeckSwipeDirection(deltaX, state.velocityX);
    const targetIndex = activeIndex + (direction === "next" ? 1 : -1);
    if (!shouldMove || targetIndex < 0 || targetIndex >= pages.length) {
      resetDrag();
      return;
    }
    if (reducedMotion) {
      resetDrag();
      setActiveIndex(targetIndex);
      return;
    }
    dragState.current = null;
    setDragOffset(direction === "next" ? -150 : 150);
    setMotionMode("throwing");
    if (throwTimer.current !== null) window.clearTimeout(throwTimer.current);
    throwTimer.current = window.setTimeout(() => {
      setActiveIndex(targetIndex);
      setDragOffset(0);
      setMotionMode("idle");
      throwTimer.current = null;
    }, THROW_DURATION);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget || motionMode === "throwing") return;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") { event.preventDefault(); move("next"); }
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") { event.preventDefault(); move("prev"); }
    if (event.key === "Home") { event.preventDefault(); setActiveIndex(0); }
    if (event.key === "End") { event.preventDefault(); setActiveIndex(Math.max(0, pages.length - 1)); }
  };

  const step = pages.length ? 360 / pages.length : 0;
  return (
    <section className={`circular-prototype${embedded ? " is-embedded" : ""}`} aria-labelledby={headingId}>
      <div className="circular-prototype-heading">
        <div><span className="eyebrow">实验原型 / CIRCULAR</span><h2 id={headingId}>让照片围着记忆转一圈。</h2><p>环形图库正在实验中：拖动、点按或使用方向键，把下一张照片带到面前。</p></div>
        <span className="circular-prototype-badge"><Aperture size={16} /> P5 / PROTOTYPE</span>
      </div>
      <div className={`circular-reader${reducedMotion ? " prefers-reduced-motion" : ""}`} role="region" aria-label="环形图库实验预览" tabIndex={0} onKeyDown={handleKeyDown}>
        {pages.length ? <div className="circular-stage" aria-label="环形照片列表">
          {pages.map((page, index) => {
            const angle = (index - activeIndex) * step + dragOffset * 0.32;
            const style: CSSProperties = { "--circular-angle": `${angle}deg`, zIndex: 100 - Math.round(Math.abs(((angle + 180) % 360) - 180)) } as CSSProperties;
            return <button type="button" key={page.id} data-index={index} className={`circular-card${index === activeIndex ? " is-active" : ""}${index === activeIndex && motionMode === "dragging" ? " is-dragging" : ""}${index === activeIndex && motionMode === "throwing" ? " is-throwing" : ""}`} style={style} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={resetDrag} onClick={() => { if (motionMode === "idle") setActiveIndex(index); }} aria-label={`${page.title || "未命名照片"}，第 ${index + 1} 张`}><PhotoFrame src={urls[page.assetId]} alt={page.title || "影集照片"} presentation={page.presentation} className="circular-card-media" loading={index === activeIndex ? "eager" : "lazy"} decoding="async" placeholder={<span className="circular-image-placeholder image-skeleton" />} /><span className="circular-card-shade" aria-hidden="true" /><span className="circular-card-copy"><small>{String(index + 1).padStart(2, "0")}</small><strong>{page.title || "未命名照片"}</strong></span></button>;
          })}
        </div> : <div className="circular-empty"><Aperture size={30} /><strong>还没有照片</strong><span>导入照片后，这里会变成一圈可以旋转的记忆。</span></div>}
        <div className="circular-controls"><button type="button" className="circular-control" onClick={() => move("prev")} disabled={!pages.length || activeIndex === 0 || motionMode === "throwing"} aria-label="上一张"><CaretLeft size={18} />上一张</button><span><strong>{pages.length ? `${String(activeIndex + 1).padStart(2, "0")} / ${String(pages.length).padStart(2, "0")}` : "空图库"}</strong><small>{reducedMotion ? "已根据系统设置停用动效" : "拖动卡片旋转"}</small></span><button type="button" className="circular-control" onClick={() => move("next")} disabled={!pages.length || activeIndex >= pages.length - 1 || motionMode === "throwing"} aria-label="下一张">下一张<CaretRight size={18} /></button></div>
      </div>
    </section>
  );
}
